/**
 * Background Console-Error-Reporter.
 *
 * Captures unhandled JavaScript-Errors, Promise-Rejections und React-
 * Hydration-Probleme im Frontend → POSTet sie im Hintergrund an
 * `/api/_internal/client-errors`. Keine UI, kein Toast.
 *
 * Schutz vor Spam:
 *   - Max. 10 Reports pro Page-Load (`MAX_REPORTS`)
 *   - Dedupe: identische Stack-Traces werden nur 1× gemeldet (`seenHashes`)
 *   - Beacon-Fallback wenn Page entladen wird (sendBeacon)
 *   - Keine Reports von /login, /setup-password (öffentliche Auth-Pages)
 *
 * Init-Bootstrap:
 *   import { initErrorReporter } from "@/lib/errorReporter";
 *   initErrorReporter();
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_REPORTS = 10;
const ENDPOINT = `${BACKEND_URL}/api/_internal/client-errors`;
const IGNORED_PATHS = ["/login", "/setup-password"];

let _reportCount = 0;
const _seenHashes = new Set();
let _initialized = false;

/** Schwacher Hash-Wert um identische Errors zu deduplizieren. */
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

/** Bestes-Effort: aus URL-Pfad das aktuelle Project/Lead extrahieren. */
function extractContext() {
  const path = window.location.pathname || "";
  const m = path.match(/\/(builder|projects|leads)\/([0-9a-f]{20,})/i);
  return {
    page_path: path,
    page_search: window.location.search || "",
    project_id: m && m[1] !== "leads" ? m[2] : null,
    lead_id: m && m[1] === "leads" ? m[2] : null,
  };
}

function shouldReport() {
  if (_reportCount >= MAX_REPORTS) return false;
  const path = window.location.pathname || "";
  if (IGNORED_PATHS.some(p => path.startsWith(p))) return false;
  return true;
}

function send(payload, useBeacon = false) {
  if (!BACKEND_URL) return;
  try {
    const body = JSON.stringify(payload);
    if (useBeacon && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    // Reporter darf NIE selbst Errors werfen — sonst Endlosschleife
  }
}

function report({ kind, message, stack, source, lineno, colno, extra }) {
  if (!shouldReport()) return;
  // Drop reporter-self-errors (Endlosschleife vermeiden)
  if ((stack || "").includes("/lib/errorReporter")) return;
  // Drop errors innerhalb des Iframe-Live-Preview (rendert Kunden-HTML, andere Domain-Logik)
  if ((source || "").includes("/preview-html") || (extra && extra.fromIframe)) return;

  const hash = hashStr((message || "") + "|" + (stack || "").split("\n")[0]);
  if (_seenHashes.has(hash)) return;
  _seenHashes.add(hash);
  _reportCount++;

  const payload = {
    kind,
    message: String(message || "").slice(0, 500),
    stack: String(stack || "").slice(0, 2500),
    source: String(source || "").slice(0, 300),
    lineno: lineno || null,
    colno: colno || null,
    user_agent: (navigator.userAgent || "").slice(0, 300),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ...extractContext(),
    ts: new Date().toISOString(),
    extra: extra || {},
  };
  send(payload);
}

/** Hooks ans `console.error` damit React-Hydration-Warnings auch ankommen. */
function patchConsoleError() {
  const orig = console.error;
  console.error = function (...args) {
    try {
      const msg = args.map(a => {
        if (a == null) return "";
        if (typeof a === "string") return a;
        if (a && a.stack) return a.stack;
        try { return JSON.stringify(a); } catch { return String(a); }
      }).join(" ");
      // Nur „interessante" Console-Errors melden:
      // - React-Hydration-Errors (ENORM wichtig — verursachen Demo-Hänger)
      // - „Warning: Cannot update a component while rendering"
      // - Unhandled Promise-Rejections die als console.error landen
      const interesting =
        /hydrat|Minified React error|cannot update.*while render|cannot be a (descendant|child)|nested.*<button>|Cannot read prop|undefined is not|null is not/i.test(msg);
      if (interesting) {
        report({
          kind: "console.error",
          message: msg.slice(0, 500),
          stack: (args.find(a => a && a.stack) || {}).stack || "",
        });
      }
    } catch (_) {}
    return orig.apply(console, args);
  };
}

export function initErrorReporter() {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;

  // 1) Synchrone JS-Errors
  window.addEventListener("error", (e) => {
    // Resource-Errors (img/script 404) ignorieren — die kommen ohne `e.error`
    if (!e.error && !e.message) return;
    report({
      kind: "window.error",
      message: e.message || "(no message)",
      stack: (e.error && e.error.stack) || "",
      source: e.filename || "",
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  // 2) Promise-Rejections die nicht .catch'd sind
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    let message = "(no message)";
    let stack = "";
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack || "";
    } else if (typeof reason === "string") {
      message = reason;
    } else if (reason) {
      try { message = JSON.stringify(reason).slice(0, 400); } catch { message = String(reason); }
    }
    report({ kind: "unhandledrejection", message, stack });
  });

  // 3) React-Hydration-Warnings via console.error patch
  patchConsoleError();

  // 4) Beim Page-Unload: aggregate „Page hatte X Errors"-Marker (mit sendBeacon)
  window.addEventListener("pagehide", () => {
    if (_reportCount > 0) {
      send(
        {
          kind: "_session_summary",
          message: `Session beendet mit ${_reportCount} Errors`,
          ...extractContext(),
          error_count: _reportCount,
          unique_errors: _seenHashes.size,
          ts: new Date().toISOString(),
        },
        true,
      );
    }
  });
}

/** Manueller Report aus einer ErrorBoundary o.ä. */
export function reportClientError(error, info = {}) {
  report({
    kind: "boundary",
    message: (error && error.message) || String(error || "(unknown)"),
    stack: (error && error.stack) || "",
    extra: info,
  });
}
